import { TabulatorFull as Tabulator } from 'tabulator-tables';
import { getCookie } from '../../utils';
import { addAlert } from '../../alerts';
import { prompt } from '../../prompts';

let pendingChanges = {};
let tabulatorInstance = null;
let saveButton = null;
let _rawData = null;
const CHANGED_CELL_CLASS = 'is-changed';

function handleCellClick(e, cell) {
    const currentVal = cell.getValue();
    const rowData = cell.getRow().getData();
    const fieldName = cell.getField();
    const measurementKey = fieldName.split('.')[1];
    const nextVal = !currentVal;

    cell.setValue(nextVal, true);

    const payload = {
        type_id: rowData.id,
        type_name: rowData.value,
        measurement_key: measurementKey,
        is_visible: nextVal,
    };

    const changeKey = `${rowData.id}-${measurementKey}`;
    pendingChanges[changeKey] = payload;

    cell.getElement().classList.add(CHANGED_CELL_CLASS);
    if (saveButton) {
        saveButton.disabled = false;
    }
}

async function saveAllChanges() {
    const changesToSave = Object.values(pendingChanges);
    if (changesToSave.length === 0) {
        addAlert('info', gettext('Brak zmian do zapisania.'));
        return;
    }

    const groupedChanges = {};
    changesToSave.forEach((change) => {
        if (!groupedChanges[change.type_name]) {
            groupedChanges[change.type_name] = [];
        }
        const label =
            _rawData.measurements[change.measurement_key] ||
            change.measurement_key;
        const stateDisplay = change.is_visible
            ? gettext('Widoczny')
            : gettext('Ukryty');
        const colorStyle = change.is_visible
            ? 'color: #28a745;'
            : 'color: #dc3545;';
        groupedChanges[change.type_name].push(
            `${label}: <strong style="${colorStyle}">${stateDisplay}</strong>`,
        );
    });

    const changesHtml = Object.entries(groupedChanges)
        .map(([typeName, changes]) => {
            return `
            <div class="change-group-summary">
                <h5>${typeName}</h5>
                <ul class="field-changes-list">
                    ${changes.map((c) => `<li>${c}</li>`).join('')}
                </ul>
            </div>
        `;
        })
        .join('');

    const result = await prompt({
        title: gettext('Potwierdź zapisanie konfiguracji'),
        html: `<div class="config-changes-container">${changesHtml}</div>`,
        width: 'auto',
    });

    if (!result.isConfirmed) return;

    if (saveButton) {
        saveButton.disabled = true;
        saveButton.textContent = gettext('Zapisywanie...');
    }

    const apiUrl = '/api/components/types/measurements-config/update/';

    try {
        const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRFTOKEN': getCookie('csrftoken'),
            },
            body: JSON.stringify(
                changesToSave.map((c) => ({
                    type_id: c.type_id,
                    measurement_key: c.measurement_key,
                    is_visible: c.is_visible,
                })),
            ),
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.message || gettext('Błąd zapisu.'));
        }

        addAlert(
            'success',
            data.message || gettext('Zmiany zapisane pomyślnie.'),
        );
        pendingChanges = {};

        tabulatorInstance.getRows().forEach((row) => {
            row.getCells().forEach((cell) => {
                cell.getElement().classList.remove(CHANGED_CELL_CLASS);
            });
        });
    } catch (error) {
        console.error('Error saving:', error);
        addAlert('error', error.message);
    } finally {
        if (saveButton) {
            saveButton.textContent = gettext('ZAPISZ ZMIANY');
            saveButton.disabled = Object.keys(pendingChanges).length === 0;
        }
    }
}

function booleanFormatter(cell, formatterParams, onRendered) {
    const value = cell.getValue();
    const element = cell.getElement();

    element.classList.remove('state-visible', 'state-hidden');
    element.innerHTML = '';

    const icon = document.createElement('i');
    icon.classList.add('fas');

    if (value === true) {
        element.classList.add('state-visible');
        element.title = gettext('Widoczny na certyfikacie');
        icon.classList.add('fa-check');
    } else {
        element.classList.add('state-hidden');
        element.title = gettext('Ukryty na certyfikacie');
        icon.classList.add('fa-times');
    }

    element.appendChild(icon);
    return element.innerHTML;
}

export function initializeMeasurementConfigTable() {
    const tableContainer = document.getElementById('measurementConfigTable');
    if (!tableContainer) return;

    saveButton = document.getElementById('save-measurement-config-btn');
    saveButton.addEventListener('click', saveAllChanges);

    fetch('/api/components/types/measurements-config/')
        .then((res) => res.json())
        .then((data) => {
            _rawData = data;

            const columns = [
                {
                    title: gettext('Grupa'),
                    field: 'group',
                    frozen: true,
                    minWidth: 150,
                    vertAlign: 'middle',
                    resizable: true,
                },
                {
                    title: gettext('Typ Podzespołu'),
                    field: 'value',
                    frozen: true,
                    minWidth: 200,
                    resizable: false,
                    vertAlign: 'middle',
                },
            ];

            data.measurement_order.forEach((key) => {
                if (data.measurements[key]) {
                    columns.push({
                        title: data.measurements[key],
                        field: `config.${key}`,
                        formatter: booleanFormatter,
                        cellClick: handleCellClick,

                        hozAlign: 'center',
                        headerHozAlign: 'center',
                        headerVertAlign: 'middle',
                        minWidth: 110,

                        headerTooltip: true,
                        cssClass: 'config-cell',
                        headerSort: false,
                        resizable: false,
                    });
                }
            });

            tabulatorInstance = new Tabulator('#measurementConfigTable', {
                data: data.types,
                columns: columns,
                layout: 'fitData',
                height: 'calc(100vh - 40rem)',
                maxHeight: '95rem',
                placeholder: gettext('Brak danych'),
                rowHeight: 40,
            });

            window.tables.measurementConfigTabulator = tabulatorInstance;
        })
        .catch((err) => {
            console.error(err);
            addAlert('error', gettext('Nie udało się pobrać konfiguracji.'));
        });
}

export default initializeMeasurementConfigTable;
